import * as THREE from "three";
import { HqMaterials } from "../hqMaterials";
import type { Biome } from "../../ui/chapters";

/** The ground detail's parts (detail.ts DetailScatter): model flattening, the decal materials, and the hashes it scatters by. */

/** How tall a tuft may be, in world units — about a third of a soldier. */
export const TUFT_HEIGHT = 0.11;

/**
 * One entry per family: every `d_` variant that ships is used, and the `p_` name is the fallback for when the
 * micro-detail set is missing (it gets scaled down to `TuftHeight`, which reads as a blob but is better than bare
 * ground).
 */
export const DETAIL_SETS: Readonly<Record<Biome, DetailFamily[]>> = {
  meadow: [
    { variants: ["d_tuft_1", "d_tuft_2", "d_tuft_3"], fallback: "p_grass_tall", weight: 0.62, cards: [0, 0, 1] },
    { variants: ["d_flowers_1", "d_flowers_2"], fallback: "p_flowers", weight: 0.11, cards: [2, 7] },
    { variants: ["d_pebbles_1", "d_pebbles_2"], fallback: "p_rubble", weight: 0.18 },
    { variants: ["d_clover", "d_fern"], fallback: "p_mushrooms", weight: 0.09, cards: [4, 3] },
  ],
  marsh: [
    // the mushroom is a highlight, not ground cover: at 0.2 of twelve per cell the marsh came out speckled orange
    { variants: ["d_reed_tuft"], fallback: "d_tuft_1", weight: 0.58, cards: [5] },
    { variants: ["d_moss_patch"], fallback: "d_clover", weight: 0.38 },
    { variants: ["d_marsh_mushroom"], fallback: "d_flowers_1", weight: 0.04 },
  ],
  ashlands: [
    { variants: ["d_ash_tuft"], fallback: "d_tuft_1", weight: 0.42, cards: [6] },
    { variants: ["d_cinder_1", "d_cinder_2"], fallback: "d_pebbles_1", weight: 0.58 },
  ],
};

/**
 * The card material: the converted `d_cards` material (wind, haze, lit extras, foliage light) plus the per-instance
 * atlas tile, and alpha-to-coverage so the cut-out edges resolve through the MSAA instead of stair-stepping (and do
 * not thin out to nothing in the distant mips the way a hard alpha test does). Cached per source material.
 */
export function cardMaterial(src: THREE.Material): THREE.Material {
  const hit = cardMats.get(src);
  if (hit) return hit;
  const m = src.clone();                                   // (clone drops the hooks: they are re-chained below)
  if (m instanceof THREE.MeshStandardMaterial) HqMaterials.standard.push({ mat: m, ground: false });   // follows the look's env
  const prev = src.onBeforeCompile, prevKey = src.customProgramCacheKey?.bind(src);
  m.onBeforeCompile = (sh, r) => {
    prev?.call(src, sh, r);
    sh.vertexShader = sh.vertexShader
      .replace("#include <common>", "#include <common>\nattribute vec2 aTile;")
      .replace("#include <uv_vertex>", "#include <uv_vertex>\n#if defined( USE_MAP ) && defined( USE_INSTANCING )\n  vMapUv += aTile;\n#endif");
  };
  m.customProgramCacheKey = () => `${prevKey?.() ?? ""}-cards`;
  m.alphaToCoverage = true;
  cardMats.set(src, m);
  return m;
}
const cardMats = new WeakMap<THREE.Material, THREE.Material>();

export interface Part { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4; norm: number }
/** A detail family: its mesh variants (and fallback), share of the scatter, and its card-atlas tiles if it has any. */
export interface DetailFamily { variants: string[]; fallback: string; weight: number; cards?: number[] }

/** Every mesh in a model with its transform relative to the model root, plus the scale that makes it TuftHeight tall. */
export function flatten(root: THREE.Object3D, name: string): Part[] {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  // A model with no extent on two axes is a line: every triangle has zero area and draws nothing. The d_ set once
  // shipped like that (tools/art/simplify_props.mjs re-quantized its unsigned positions onto one axis), and instancing
  // it across the ground cost over a million invisible triangles a frame. Kept as a guard; tools/art/check-glb.py
  // now fails the bake on such a model.
  const size = box.getSize(new THREE.Vector3());
  if ([size.x, size.y, size.z].filter((v) => v < DEGENERATE).length >= 2) return [];
  const height = Math.max(1e-3, box.max.y - box.min.y);
  const norm = name.startsWith("d_") ? 1 : TUFT_HEIGHT / height;   // the d_ set is authored at size
  const out: Part[] = [];
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const local = new THREE.Matrix4().copy(mesh.matrixWorld);
    const material = mesh.material as THREE.Material;
    if (name.startsWith("d_")) litFromAbove(material);
    out.push({ geometry: mesh.geometry, material, local, norm });
  });
  return out;
}

/**
 * Lights both faces of a decal alike. The d_ bake leans every blade and petal normal toward straight up so a tuft is
 * lit like the turf it grows from (tools/art/decals.mjs); three.js flips a double-sided material's normal on back
 * faces, which turned those normals to point down, and every petal and blade seen from behind drew black.
 * The d_ materials are used by the scatter alone, so they are changed in place, once.
 */
function litFromAbove(material: THREE.Material): void {
  if (litUp.has(material)) return;
  litUp.add(material);
  const prev = material.onBeforeCompile, prevKey = material.customProgramCacheKey.bind(material);
  material.onBeforeCompile = (sh, r) => {
    prev.call(material, sh, r);
    sh.fragmentShader = sh.fragmentShader.replace("#include <normal_fragment_begin>",
      THREE.ShaderChunk.normal_fragment_begin.replace("gl_FrontFacing ? 1.0 : - 1.0", "1.0"));
  };
  material.customProgramCacheKey = () => `${prevKey()}|lit-up`;
  material.needsUpdate = true;
}
const litUp = new WeakSet<THREE.Material>();

/** Below this extent (world units) a model's axis counts as flat. */
const DEGENERATE = 1e-5;

export function hash3(x: number, y: number, k: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7 + k * 74.7) * 43758.5453;
  return s - Math.floor(s);
}
export function frac(v: number): number { const s = Math.sin(v * 91.7) * 43758.5453; return s - Math.floor(s); }
function hash2(x: number, y: number): number { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); }
export function vnoise(x: number, y: number): number {
  const ix = Math.floor(x), iy = Math.floor(y); let fx = x - ix, fy = y - iy;
  fx = fx * fx * (3 - 2 * fx); fy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy), b = hash2(ix + 1, iy), c = hash2(ix, iy + 1), d = hash2(ix + 1, iy + 1);
  return (a + (b - a) * fx) * (1 - fy) + (c + (d - c) * fx) * fy;
}
